import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Zap, Timer, Check, X, RotateCcw, Home } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";
import { CountUp } from "../../components/common/CountUp";

// Visuele "groepjes": a groepjes van b stippen (alleen bij kleine producten).
function TafelDots({ a, b }) {
  const groups = Math.min(a, b);
  const per = Math.max(a, b);
  if (groups * per > 40) return null;
  return (
    <div className="flex flex-wrap justify-center gap-3 mb-5" data-testid="tafel-dots">
      {[...Array(groups)].map((_, g) => (
        <div key={g} className="rounded-2xl p-2 grid gap-1" style={{ background: "var(--t-surface2)", gridTemplateColumns: `repeat(${Math.min(per, 5)}, minmax(0, 1fr))` }}>
          {[...Array(per)].map((_, d) => (
            <span key={d} className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--t-accent)" }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function TafelSprint() {
  const navigate = useNavigate();
  const { theme, terms } = useTheme();
  const { refresh } = useAuth();
  const [phase, setPhase] = useState("setup"); // setup | play | done
  const [allowed, setAllowed] = useState([]);
  const [picked, setPicked] = useState([]);
  const [seconds, setSeconds] = useState(60);
  const [facts, setFacts] = useState([]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(0);
  const [left, setLeft] = useState(60);
  const [flash, setFlash] = useState(null); // 'ok' | 'no'
  const [result, setResult] = useState(null);
  const [starting, setStarting] = useState(false);
  const tick = useRef(null);
  const lock = useRef(false);
  const finished = useRef(false);

  useEffect(() => {
    api.get("/child/sprint/options").then(({ data }) => {
      setAllowed(data.tables); setPicked(data.tables);
    }).catch(() => {});
  }, []);

  const toggleTable = (t) => {
    sfx.click();
    setPicked((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]);
  };

  const begin = async () => {
    if (picked.length === 0) { toast.error("Kies minstens 1 tafel"); return; }
    setStarting(true);
    try {
      const { data } = await api.post("/child/sprint/start", { tables: picked, seconds });
      finished.current = false;
      setFacts(data.facts); setLeft(data.seconds); setSeconds(data.seconds);
      setIdx(0); setScore(0); setAnswered(0); setPhase("play");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setStarting(false); }
  };

  // countdown
  useEffect(() => {
    if (phase !== "play") return;
    tick.current = setInterval(() => setLeft((s) => s - 1), 1000);
    return () => clearInterval(tick.current);
  }, [phase]);

  useEffect(() => {
    if (phase === "play" && left <= 0) { clearInterval(tick.current); finish(); }
    // eslint-disable-next-line
  }, [left, phase]);

  const finish = async (finalScore, finalAnswered) => {
    if (finished.current) return;
    finished.current = true;
    clearInterval(tick.current);
    const correct = finalScore != null ? finalScore : score;
    const tot = finalAnswered != null ? finalAnswered : answered;
    setPhase("done");
    try {
      const { data } = await api.post("/child/sprint/finish", { correct, total: tot, duration: seconds });
      setResult(data);
      const pct = tot ? Math.round((correct / tot) * 100) : 0;
      if (pct >= 80) sfx.win(); else sfx.coin();
      refresh();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const choose = (opt) => {
    if (lock.current || finished.current) return;
    const fact = facts[idx];
    const ok = opt === fact.answer;
    lock.current = true;
    setFlash(ok ? "ok" : "no");
    if (ok) sfx.correct(); else sfx.wrong();
    const newScore = score + (ok ? 1 : 0);
    const newAnswered = answered + 1;
    setScore(newScore); setAnswered(newAnswered);
    setTimeout(() => {
      setFlash(null);
      lock.current = false;
      setIdx((i) => {
        if (i + 1 >= facts.length || left <= 0) {
          setTimeout(() => finish(newScore, newAnswered), 0);
          return i;
        }
        return i + 1;
      });
    }, 420);
  };

  const muted = { color: "var(--t-muted)" };

  // ---------- SETUP ----------
  if (phase === "setup") {
    return (
      <ThemedShell>
        <header className="sticky top-0 z-30 border-b backdrop-blur-md" style={{ background: `color-mix(in srgb, var(--t-surface) 75%, transparent)`, borderColor: "var(--t-border)" }}>
          <div className="max-w-3xl mx-auto px-5 h-16 flex items-center">
            <Button variant="ghost" onClick={() => navigate("/leerling")} className="rounded-full font-bold" style={{ color: "var(--t-text)" }} data-testid="sprint-back-btn"><ArrowLeft className="w-5 h-5 mr-1" /> {terms.dashboard}</Button>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-5 pt-10 pb-16 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Zap className="w-10 h-10" strokeWidth={2.5} /></motion.div>
          <h1 className="font-heading text-3xl font-black themed-heading">Tafel-sprint ⚡</h1>
          <p className="font-semibold mt-1" style={muted}>Hoeveel tafelsommen haal je voordat de tijd op is?</p>

          <ThemedCard className="p-6 mt-8 text-left">
            <p className="font-heading font-bold mb-3">Kies je tafels</p>
            <div className="flex flex-wrap gap-2.5 mb-6">
              {allowed.map((t) => {
                const sel = picked.includes(t);
                return (
                  <button key={t} onClick={() => toggleTable(t)} data-testid={`sprint-table-${t}`}
                    className="px-5 py-3 rounded-2xl font-heading font-extrabold text-lg border-2 transition-all active:scale-95"
                    style={sel ? { background: "var(--t-primary)", color: "var(--t-on-primary)", borderColor: "var(--t-primary)" } : { background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                    × {t}
                  </button>
                );
              })}
            </div>
            <p className="font-heading font-bold mb-3">Hoe lang?</p>
            <div className="flex gap-2.5 mb-6">
              {[45, 60, 90].map((s) => (
                <button key={s} onClick={() => { sfx.click(); setSeconds(s); }} data-testid={`sprint-seconds-${s}`}
                  className="px-5 py-2.5 rounded-full font-bold border-2 transition-all active:scale-95"
                  style={seconds === s ? { background: "var(--t-accent)", color: "var(--t-on-accent)", borderColor: "var(--t-accent)" } : { background: "var(--t-surface)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                  {s}s
                </button>
              ))}
            </div>
            <Button onClick={begin} disabled={starting} className="w-full h-14 rounded-2xl font-bold text-lg border-0 active:scale-95" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="sprint-start-btn">
              {starting ? "Laden..." : <>Start de sprint! <Zap className="w-5 h-5 ml-1.5" /></>}
            </Button>
          </ThemedCard>
        </main>
      </ThemedShell>
    );
  }

  // ---------- DONE ----------
  if (phase === "done") {
    const tot = result?.total ?? answered;
    const sc = result?.score ?? score;
    const pct = tot ? Math.round((sc / tot) * 100) : 0;
    const great = pct >= 80;
    return (
      <ThemedShell>
        <div className="max-w-2xl mx-auto px-5 pt-12 pb-16">
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <ThemedCard className="p-8 text-center">
              <motion.div className="text-6xl mb-3" animate={great ? { scale: [0, 1.3, 1], rotate: [-20, 10, 0] } : { scale: 1 }} transition={{ duration: 0.8 }}>{great ? "🏆" : theme.mascot}</motion.div>
              <h1 className="font-heading text-3xl font-black themed-heading">{great ? "Tafelkampioen!" : sc > 0 ? "Goed gedaan!" : "Blijf oefenen!"}</h1>
              <p className="mt-2 font-semibold" style={muted}>Je had <b>{sc}</b> van de <b>{tot}</b> goed</p>
              <div className="my-6 font-heading text-6xl font-black themed-heading" data-testid="sprint-score">{sc}</div>
              <div className="flex justify-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="sprint-xp"><span className="font-heading font-extrabold">+<CountUp value={result?.xp_earned || 0} delay={250} /> {terms.xp}</span></div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="sprint-coins"><span className="text-lg">{terms.coinIcon}</span><span className="font-heading font-extrabold">+<CountUp value={result?.coins_earned || 0} delay={400} /> {terms.coins}</span></div>
              </div>
              {result?.new_badges?.length > 0 && (
                <div className="mt-6 p-4 rounded-2xl" style={{ background: "var(--t-surface2)" }}>
                  <p className="font-heading font-bold">Nieuwe {terms.badges.toLowerCase()}! 🎉</p>
                  <div className="flex justify-center gap-2 mt-3 flex-wrap">
                    {result.new_badges.map((b) => <span key={b.id} className="px-3 py-1.5 rounded-full font-bold text-sm" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}>{b.title}</span>)}
                  </div>
                </div>
              )}
              <div className="flex gap-3 mt-8">
                <Button onClick={() => setPhase("setup")} className="flex-1 h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="sprint-again-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Nog een keer</Button>
                <Button onClick={() => navigate("/leerling")} className="flex-1 h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }} data-testid="sprint-home-btn"><Home className="w-4 h-4 mr-1.5" /> {terms.dashboard}</Button>
              </div>
            </ThemedCard>
          </motion.div>
        </div>
      </ThemedShell>
    );
  }

  // ---------- PLAY ----------
  const fact = facts[idx] || { a: 0, b: 0, options: [] };
  const pctLeft = Math.max(0, (left / seconds) * 100);
  return (
    <ThemedShell>
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-16">
        <div className="flex items-center justify-between mb-3">
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-heading font-extrabold" style={{ background: "var(--t-surface2)", color: left <= 10 ? "var(--t-primary)" : "var(--t-text)" }} data-testid="sprint-timer"><Timer className="w-4 h-4" /> {left}s</span>
          <span className="font-heading font-extrabold" data-testid="sprint-live-score">⭐ {score}</span>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden mb-8" style={{ background: "var(--t-surface2)" }}>
          <motion.div className="h-full rounded-full" style={{ background: "var(--t-accent)" }} animate={{ width: `${pctLeft}%` }} transition={{ ease: "linear", duration: 0.9 }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.18 }}>
            <ThemedCard className="p-7 text-center" style={flash === "ok" ? { borderColor: "var(--t-success)" } : flash === "no" ? { borderColor: "var(--t-primary)" } : {}}>
              <TafelDots a={fact.a} b={fact.b} />
              <p className="font-heading text-4xl sm:text-5xl font-black themed-heading mb-6" data-testid="sprint-question">{fact.a} × {fact.b} = ?</p>
              <div className="grid grid-cols-2 gap-3">
                {fact.options.map((o, j) => (
                  <button key={j} onClick={() => choose(o)} data-testid={`sprint-opt-${j}`}
                    className="h-16 rounded-2xl font-heading font-black text-2xl border-2 transition-all active:scale-95"
                    style={{ background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                    {o}
                  </button>
                ))}
              </div>
            </ThemedCard>
          </motion.div>
        </AnimatePresence>

        {flash && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="fixed inset-x-0 top-24 flex justify-center pointer-events-none z-50">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: flash === "ok" ? "var(--t-success)" : "var(--t-primary)", color: "#fff" }}>
              {flash === "ok" ? <Check className="w-8 h-8" strokeWidth={3} /> : <X className="w-8 h-8" strokeWidth={3} />}
            </div>
          </motion.div>
        )}
      </div>
    </ThemedShell>
  );
}
